import { API_BASE_URL } from '@src/runtimeConfig'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowLeft, XClose } from '@untitled-ui/icons-react'
import { MyButton, myToaster } from '@interstellar-component'
import { formatFileSize } from '@src/services/Helper'
import FileViewer from '@src/components/FileViewer'
import AttachmentRow from '@src/components/AttachmentRow'
import AuthImage from '@src/components/AuthImage'

import { useApp } from '../../AppContext'
import { CompanyService } from './service'
import CompanyCredentialForm from './components/CompanyCredentialForm'
import AgreementCard from './components/AgreementCard'

// Stored file paths already include the service homepage prefix (e.g.
// "/backoffice/uploads/.."), which VITE_API_BASE_URL also contains — prepend only
// the ORIGIN to avoid a doubled "/backoffice/backoffice" (mirrors Information.jsx).
function absoluteFileUrl(path) {
  if (!path || typeof path !== 'string') return ''
  if (/^https?:\/\//i.test(path)) return path
  const apiBase = API_BASE_URL ?? ''
  let origin
  try {
    origin = new URL(apiBase, window.location.origin).origin
  } catch {
    origin = apiBase.replace(/\/+$/, '')
  }
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

const DOC_LABELS = {
  akta_perubahan_terbaru: 'Akta Perubahan Terbaru',
  akta_pendirian: 'Akta Pendirian',
  npwp_doc: 'NPWP',
  nib_doc: 'NIB',
}
const DOC_ORDER = ['akta_perubahan_terbaru', 'akta_pendirian', 'npwp_doc', 'nib_doc']

function Card({ title, action, children }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  )
}

function Row({ label, value }) {
  const display = value === null || value === undefined || value === '' ? '—' : value
  return (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-3 last:border-b-0">
      <span className="shrink-0 text-sm text-gray-500">{label}</span>
      <span className="min-w-0 break-all text-right text-sm font-medium text-gray-900">{display}</span>
    </div>
  )
}

export default function CompanyProfile() {
  const { companyContext } = useApp()
  const nav = useNavigate()

  const single = companyContext?.count === 1 ? companyContext.companies?.[0] : null
  const companyId = single?.id

  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [viewerDoc, setViewerDoc] = useState(null)
  const [logoBusy, setLogoBusy] = useState(false)
  const fileRef = useRef(null)

  const load = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const [infoRes, resultRes] = await Promise.all([
        CompanyService.getDetailCompany(companyId, { type: 'information' }),
        CompanyService.getDetailCompany(companyId, { type: 'result' }),
      ])
      const info = infoRes?.data !== undefined ? infoRes.data : infoRes
      const result = resultRes?.data !== undefined ? resultRes.data : resultRes
      setDetail({ ...info, general_information: result?.general_information })
    } catch (e) {
      myToaster(e)
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => {
    load()
  }, [load])

  // Only tenants with a single company reach the profile from the single-view.
  if (companyContext && companyContext.count !== 1) {
    return <Navigate to="/company" replace />
  }

  const ci = detail?.company_information || {}
  const cd = detail?.compliance_documents || {}
  const rep = detail?.authorized_representative || {}
  const logoUrl = absoluteFileUrl(ci.logo_url)

  const docs = DOC_ORDER.map((key) => {
    const doc = cd[key]
    return doc && doc.file_path ? { key, ...doc } : null
  }).filter(Boolean)

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !companyId) return
    setLogoBusy(true)
    try {
      const fd = new FormData()
      fd.append('logo', file)
      const up = await CompanyService.uploadLogo(fd)
      const url = up?.data?.url ?? up?.url
      await CompanyService.updateCompany(companyId, { logo_url: url })
      myToaster({ message: 'Logo updated.', type: 'success' })
      await load()
    } catch (err) {
      myToaster(err)
    } finally {
      setLogoBusy(false)
    }
  }

  const handleLogoDelete = async () => {
    if (!companyId || logoBusy) return
    setLogoBusy(true)
    try {
      await CompanyService.updateCompany(companyId, { remove_logo: true })
      myToaster({ message: 'Logo removed.', type: 'success' })
      await load()
    } catch (err) {
      myToaster(err)
    } finally {
      setLogoBusy(false)
    }
  }

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <header className="relative flex flex-col gap-4 border-b border-gray-200 px-8 pb-5 pt-6">
        <button
          type="button"
          aria-label="Close"
          // replace (not push) so returning to /company doesn't stack a second
          // entry that makes Company Information's "Kembali" bounce back here.
          onClick={() => nav('/company', { replace: true })}
          className="absolute right-6 top-5 flex size-10 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-50"
        >
          <XClose className="size-5" stroke="currentColor" />
        </button>
        <button
          type="button"
          onClick={() => nav('/company', { replace: true })}
          className="flex w-fit items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="size-5" stroke="currentColor" /> Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Company profile</h1>
      </header>

      <div className="grid min-h-0 flex-1 gap-6 overflow-auto p-8 lg:grid-cols-3">
        {/* Column 1 — API credentials + the membership agreement they wait on
            (locked until CLIK has countersigned it). */}
        <div className="flex flex-col gap-6">
          <CompanyCredentialForm
            companyId={companyId}
            company={detail}
            generalInformation={detail?.general_information}
            onValidated={load}
            onAgreementChanged={load}
          />
          {/* The credential form above already says what happens to a
              declined / voided agreement. */}
          <AgreementCard company={detail} onView={setViewerDoc} showEndedNote={false} />
        </div>

        {/* Column 2 — Company information (+ logo Update/Delete) */}
        <div className="flex flex-col gap-6">
          <Card title="Company Information">
            <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <AuthImage
                    src={logoUrl}
                    alt=""
                    className="size-16 rounded-full border border-gray-200 object-cover"
                  />
                ) : (
                  <div className="flex size-16 items-center justify-center rounded-full bg-brand/50 text-lg font-semibold text-brand/700">
                    {(ci.legal_name || ci.name || '?').trim().charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-sm text-gray-500">Company logo</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={logoBusy || !logoUrl}
                  onClick={handleLogoDelete}
                  className="text-sm font-semibold text-gray-600 hover:text-error/700 disabled:opacity-40"
                >
                  Delete
                </button>
                <button
                  type="button"
                  disabled={logoBusy}
                  onClick={() => fileRef.current?.click()}
                  className="text-sm font-semibold text-brand/700 hover:text-brand/800 disabled:opacity-40"
                >
                  {logoBusy ? 'Saving…' : 'Update'}
                </button>
              </div>
            </div>
            <Row label="Company Legal Name" value={ci.legal_name} />
            <Row label="Business / Brand Name" value={ci.name} />
            <Row label="Business Entity Type" value={ci.business_category} />
            <Row label="Industry" value={ci.industry} />
            <Row label="Number of Employees" value={ci.number_of_employees} />
            <Row label="Registration Number (NIB)" value={cd.nib} />
            <Row label="NPWP" value={cd.npwp} />
            <Row label="Company Address" value={ci.company_address} />
            <Row label="Company Email" value={ci.email} />
          </Card>
        </div>

        {/* Column 3 — Authorized representative + attachments */}
        <div className="flex flex-col gap-6">
          <Card title="Authorized Representative (PIC)">
            <Row label="Full Name" value={rep.name} />
            <Row label="Job Title" value={rep.job_title} />
            <Row label="Email Address" value={rep.email} />
            <Row label="Phone Number" value={rep.phone} />
            {/* ID Type / ID Number hidden until PIC identity capture is built —
                there is no input for them yet, so they always render "—". */}
          </Card>

          {docs.length > 0 ? (
            <Card title="Attachments">
              <div className="p-2">
                {docs.map((doc, index) => (
                  <AttachmentRow
                    key={doc.key}
                    name={doc.file_name || DOC_LABELS[doc.key] || doc.document_type}
                    mimeType={doc.mime_type}
                    meta={doc.file_size ? formatFileSize(Number(doc.file_size)) : null}
                    onClick={() =>
                      setViewerDoc({
                        url: absoluteFileUrl(doc.file_path),
                        fileName: doc.file_name || DOC_LABELS[doc.key] || doc.document_type,
                        mimeType: doc.mime_type,
                      })
                    }
                    last={index === docs.length - 1}
                  />
                ))}
              </div>
            </Card>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-6 text-center text-sm text-gray-400">
          Loading…
        </div>
      ) : null}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/gif"
        className="hidden"
        onChange={handleLogoChange}
      />

      <FileViewer
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        fileName={viewerDoc?.fileName}
        mimeType={viewerDoc?.mimeType}
      />
    </div>
  )
}
